import React, { useState, useEffect } from 'react';
import { X, Edit3, Music, Clock, Zap, Key, Mic, Tag, Users, Sparkles, Play, Film, Calendar, ShieldAlert, Hash } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TrackDetailsModal({ track, token, onClose, onEdit }) {
  const meta = track.final_metadata || {};
  // Heavy fields (lyrics) excluded from list endpoint for perf — pull them in on open.
  const [lyrics, setLyrics] = useState(track.lyrics || null);
  useEffect(() => {
    if (track.lyrics !== undefined && track.lyrics !== null) return; // already have it
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/tracks/${track.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok && !cancelled) {
          const full = await res.json();
          if (full.lyrics) setLyrics(full.lyrics);
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [track.id, track.lyrics, token]);

  const MetadataItem = ({ icon: Icon, label, value, className = '' }) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    
    const displayValue = Array.isArray(value) ? value.join(', ') : value;
    
    return (
      <div className={`p-4 bg-white/5 rounded-xl border border-white/10 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4" style={{color: '#F96F51'}} />
          <span className="text-xs font-medium text-white/40 uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-white/90">{displayValue}</p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop" data-testid="track-details-modal">
      <div className="glass-card rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeInScale border border-white/10">
        {/* Header */}
        <div className="sticky top-0 z-10 glass border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.3), rgba(249,111,81,0.1))'}}>
              <Music className="w-6 h-6" style={{color: '#F96F51'}} />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-white">{meta.title || track.filename}</h2>
              {meta.artist && <p className="text-sm text-white/50">{meta.artist}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl hover:bg-white/10 transition-colors"
              data-testid="edit-track-button"
            >
              <Edit3 className="w-4 h-4 text-white/70" />
              <span className="text-white/70">Edit</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              data-testid="close-modal"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Description */}
          {meta.description && (
            <div className="mb-6 p-5 rounded-xl border border-white/10" style={{background: 'linear-gradient(135deg, rgba(249,111,81,0.1), rgba(249,111,81,0.02))'}}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5" style={{color: '#F96F51'}} />
                <h3 className="font-heading font-bold text-white">Sync Description</h3>
              </div>
              <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{meta.description}</p>
            </div>
          )}

          {/* Lyrics */}
          {lyrics && (
            <div className="mb-6 p-5 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <Mic className="w-5 h-5" style={{color: '#F96F51'}} />
                <h3 className="font-heading font-bold text-white">Lyrics</h3>
              </div>
              <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
                {lyrics}
              </p>
            </div>
          )}

          {/* Core Metadata Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <MetadataItem icon={Clock} label="Tempo" value={meta.tempoBPM ? `${Math.round(meta.tempoBPM)} BPM` : null} />
            <MetadataItem icon={Key} label="Key" value={meta.key} />
            <MetadataItem icon={Hash} label="Time Signature" value={meta.timeSignature} />
            <MetadataItem icon={Zap} label="Energy Level" value={meta.energyLevel} />
            <MetadataItem icon={Play} label="Energy Dynamics" value={meta.energyDynamics} />
            <MetadataItem icon={Calendar} label="Musical Era" value={meta.musicalEra} />
            <MetadataItem icon={Calendar} label="Production Year" value={meta.estimatedProductionYear ? String(meta.estimatedProductionYear) : null} />
            <MetadataItem icon={ShieldAlert} label="AI-Generated" value={meta.aiGenerated === true ? 'Yes' : (meta.aiGenerated === false && meta.aiScore !== undefined && meta.aiScore !== null ? 'No' : null)} />
          </div>

          {/* Vocals Info */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <MetadataItem icon={Mic} label="Vocals" value={meta.vocals} />
            <MetadataItem icon={Mic} label="Voice Presence" value={meta.voicePresenceProfile} />
            <MetadataItem icon={Users} label="Voice Gender" value={meta.predominantVoiceGender} />
          </div>

          {/* Tags Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            <MetadataItem icon={Tag} label="Genres" value={meta.genre} />
            <MetadataItem icon={Tag} label="Subgenres" value={meta.subgenres} />
            <MetadataItem icon={Sparkles} label="Mood Tags" value={meta.moodTags} />
            <MetadataItem icon={Sparkles} label="Advanced Mood Tags" value={meta.moodAdvancedTags} />
            <MetadataItem icon={Music} label="Instruments" value={meta.instrumentTags} />
            <MetadataItem icon={Film} label="Scene Suggestions" value={meta.sceneSuggestions} />
            <MetadataItem icon={Users} label="Similar Artists" value={meta.similarArtists} />
            <MetadataItem icon={Tag} label="Character Tags" value={meta.characterTags} />
            <MetadataItem icon={Play} label="Movement Tags" value={meta.movementTags} />
            <MetadataItem icon={Zap} label="Emotion" value={meta.emotion} />
            <MetadataItem icon={Play} label="Emotional Dynamics" value={meta.emotionalDynamics} />
          </div>
        </div>
      </div>
    </div>
  );
}
